import * as tslib_1 from "tslib";
import { isDevMode, NgZone, Injector } from '@angular/core';
import { Router } from '@angular/router';
import { AppInsightsService } from '@markpieszak/ng-application-insights';
import { HttpErrorResponse } from '@angular/common/http';
import forIn from 'lodash-es/forIn';
import { Observable, throwError, EMPTY, BehaviorSubject, from } from 'rxjs';
import { ProfileService } from '../profile/profile.service';
import { WindowService } from '../window/window.service';
import { VideoPortalUser } from './video-portal-user.model';
import { BroadcastService, MsalService } from '@azure/msal-angular';
import { Logger, Account, AuthError, AuthResponse } from 'msal';
import { take, catchError, shareReplay } from 'rxjs/operators';
import { AuthConfigService } from '../auth-config/auth-config.service';
import { ReturnUrlService } from '../return-url/return-url.service';
import { SpinnerService } from '../spinner/spinner.service';
var AuthService = /** @class */ (function () {
    function AuthService(windowService, broadcastService, msalService, zone, authConfigService, injector, spinnerService) {
        var _this = this;
        this.windowService = windowService;
        this.broadcastService = broadcastService;
        this.msalService = msalService;
        this.zone = zone;
        this.authConfigService = authConfigService;
        this.injector = injector;
        this.spinnerService = spinnerService;
        this.msalInitPromise = null;
        this._userInfoSubject = new BehaviorSubject(null);
        this._loggedIn = false;
        this.authRedirectCount = 0;
        this.lastRedirectTime = 0;
        this.MAX_REDIRECTS = 3;
        this.REDIRECT_WINDOW_MS = 10000; // 10 seconds
        this.tokenAcquisitionInProgress = false;
        this.currentTokenPromise = null;
        this._loginSuccessInProgress = false;
        this.msalInitialized = false;
        this.loginInProgress = false;
        // Check for login in progress from previous session
        this.loginInProgress = sessionStorage.getItem('login_in_progress') === 'true';
        if (this.loginInProgress) {
            // Clear after a delay to prevent permanent lockout
            setTimeout(function () {
                _this.loginInProgress = false;
                sessionStorage.removeItem('login_in_progress');
            }, 5000);
        }
        // Register the redirect callback immediately in the constructor
        this.registerRedirectCallback();
    }
    AuthService.prototype.initMSAL = function () {
        return tslib_1.__awaiter(this, void 0, void 0, function () {
            var isProcessingRedirect, account;
            var _this = this;
            return tslib_1.__generator(this, function (_a) {
                switch (_a.label) {
                    case 0:
                        if (this.msalInitialized) {
                            if (isDevMode()) {
                                console.log('MSAL already initialized, returning existing promise.');
                            }
                            // Return the existing promise OR a resolved one if somehow null
                            return [2 /*return*/, this.msalInitPromise || Promise.resolve()];
                        }
                        // Create initialization promise to prevent race conditions
                        if (!this.msalInitPromise) {
                            this.msalInitPromise = new Promise(function (resolve) { return tslib_1.__awaiter(_this, void 0, void 0, function () {
                                return tslib_1.__generator(this, function (_a) {
                                    if (isDevMode()) {
                                        console.log('Initializing MSAL');
                                    }
                                    // Set up broadcast listeners before checking account to ensure we catch all events
                                    this.setupBroadcastListeners();
                                    // Set up logger
                                    this.msalService.setLogger(new Logger(function (logLevel, message, piiEnabled) {
                                        if (isDevMode()) {
                                            console.log('MSAL Logging: ', message);
                                        }
                                    }, {
                                        piiLoggingEnabled: false,
                                    }));
                                    // Check account status -- MOVED TO handleLoginSuccess AND post-init check below
                                    this.msalInitialized = true;
                                    resolve(); // Resolve after basic setup is complete
                                    return [2 /*return*/];
                                });
                            }); });
                        }
                        // Wait for the basic initialization promise to resolve
                        return [4 /*yield*/, this.msalInitPromise];
                    case 1:
                        // Wait for the basic initialization promise to resolve
                        _a.sent();
                        // === NEW LOGIC: Check account AFTER basic MSAL init completes ===
                        if (isDevMode()) {
                            console.log('MSAL init complete. Checking for existing account.');
                        }
                        isProcessingRedirect = this.windowService.window.location.hash.includes('#code=') || this.windowService.window.location.hash.includes('#error=');
                        if (!isProcessingRedirect) {
                            account = this.msalService.getAccount();
                            // Check if account exists AND we are not already logged in (or login success isn't already processing)
                            if (account && !this.isLoggedIn && !this._loginSuccessInProgress) {
                                if (isDevMode()) {
                                    console.log('Existing account found on init/refresh, attempting to restore session.', account);
                                }
                                // Use zone.run to ensure Angular change detection picks it up
                                this.zone.run(function () {
                                    // Treat this like a login success scenario, but without a specific AuthResponse.
                                    // Call handleLoginSuccess, ensuring the check inside it prevents double execution.
                                    _this.handleLoginSuccess(null); // Pass null to indicate session restore
                                });
                            }
                            else if (!account) {
                                if (isDevMode()) {
                                    console.log('No existing account found on init/refresh.');
                                }
                            }
                            else if (this._loginSuccessInProgress) {
                                if (isDevMode()) {
                                    console.warn("Login success already processing during init check, skipping session restore attempt.");
                                }
                            }
                        }
                        else {
                            if (isDevMode()) {
                                console.log('MSAL redirect hash detected in URL, skipping initial account check (expecting broadcast).');
                            }
                        }
                        // =============================================================
                        // Return the original promise after the check is initiated
                        return [2 /*return*/, this.msalInitPromise];
                }
            });
        });
    };
    AuthService.prototype.login = function () {
        var _this = this;
        // Ensure MSAL is initialized first
        this.initMSAL().then(function () {
            // Don't attempt login if one is already in progress
            if (_this.loginInProgress) {
                console.log('Login already in progress, ignoring duplicate request');
                return;
            }
            var now = Date.now();
            // Reset counter if enough time has passed
            if (now - _this.lastRedirectTime > _this.REDIRECT_WINDOW_MS) {
                _this.authRedirectCount = 0;
            }
            _this.authRedirectCount++;
            _this.lastRedirectTime = now;
            if (_this.authRedirectCount > _this.MAX_REDIRECTS) {
                console.error('Auth redirect loop detected - breaking cycle');
                // Get Router via injector
                var router = _this.injector.get(Router);
                router.navigate(['error'], { queryParams: { reason: 'auth_loop' } });
                return;
            }
            // Reset login success flag before starting redirect
            _this._loginSuccessInProgress = false;
            // Set flag before redirecting
            _this.loginInProgress = true;
            // Store flag in sessionStorage to persist across redirects
            sessionStorage.setItem('login_in_progress', 'true');
            // Add timeout to reset flag in case redirect fails
            setTimeout(function () {
                _this.loginInProgress = false;
                sessionStorage.removeItem('login_in_progress');
            }, 30000); // 30 second timeout
            _this.msalService.loginRedirect();
        });
    };
    AuthService.prototype.logout = function () {
        console.log("Calling msalService.logout()...");
        // Clear local state
        this._loggedIn = false;
        this._userInfoSubject.next(null);
        // Clear app insights context
        var ai = this.injector.get(AppInsightsService);
        ai.clearAuthenticatedUserContext();
        // Reset flags on logout
        this._loginSuccessInProgress = false;
        // Clear local storage, including returnUrl
        this.clearLocalStorage();
        // Let MSAL handle the actual logout
        this.msalService.logout();
    };
    AuthService.prototype.checkoutAccount = function () {
        var account = this.msalService.getAccount();
        this._loggedIn = !!account;
        if (isDevMode()) {
            console.log('this._loggedIn: ' + this._loggedIn);
        }
    };
    AuthService.prototype.getBearerToken = function (resource) {
        var _this = this;
        // Use the async/await pattern consistently inside the from() conversion
        return from((function () { return tslib_1.__awaiter(_this, void 0, void 0, function () {
            var userAccount, response, error_1, isInteractionRequired;
            return tslib_1.__generator(this, function (_a) {
                switch (_a.label) {
                    case 0: return [4 /*yield*/, this.initMSAL()];
                    case 1:
                        _a.sent();
                        // If we already have a token acquisition in progress, return that promise
                        if (this.tokenAcquisitionInProgress && this.currentTokenPromise) {
                            return [2 /*return*/, this.currentTokenPromise];
                        }
                        this.tokenAcquisitionInProgress = true;
                        userAccount = this.msalService.getAccount();
                        if (!userAccount) {
                            this.tokenAcquisitionInProgress = false;
                            throw new Error('User account not found for acquiring token silently.');
                        }
                        _a.label = 2;
                    case 2:
                        _a.trys.push([2, 4, , 5]);
                        return [4 /*yield*/, this.msalService.acquireTokenSilent({
                                scopes: resource,
                            })];
                    case 3:
                        response = _a.sent();
                        if (isDevMode()) {
                            console.log('Acquire token silent completed.');
                        }
                        this.windowService.localStorage.setItem("msal.access_token", response.accessToken);
                        this.tokenAcquisitionInProgress = false;
                        return [2 /*return*/, response];
                    case 4:
                        error_1 = _a.sent();
                        this.tokenAcquisitionInProgress = false;
                        this.currentTokenPromise = null;
                        isInteractionRequired = (error_1 && error_1.name === 'InteractionRequiredAuthError') ||
                            (error_1 && error_1.errorCode === 'interaction_required') ||
                            (error_1 && error_1.errorCode === 'login_required') ||
                            (error_1 && error_1.errorCode === 'consent_required');
                        if (isInteractionRequired) {
                            if (isDevMode()) {
                                console.warn('Interaction required during silent token acquisition. Triggering interactive login...', error_1);
                            }
                            // Interactive recovery for guest accounts - redirect to login
                            this.msalService.loginRedirect({ scopes: resource });
                            return [2 /*return*/, Promise.reject(error_1)]; // redirect navigates away
                        }
                        throw error_1;
                    case 5: return [2 /*return*/];
                }
            });
        }); })()).pipe(
        // Share the observable to prevent multiple token requests for the same resource
        shareReplay(1));
    };
    AuthService.prototype.handleCallback = function () {
        var _this = this;
        this.initMSAL().then(function () {
            _this.msalService.loginRedirect();
        });
    };
    AuthService.prototype.refresh = function () {
        return tslib_1.__awaiter(this, void 0, void 0, function () {
            var profileService, error_2, router;
            return tslib_1.__generator(this, function (_a) {
                switch (_a.label) {
                    case 0:
                        console.log("AuthService: Refreshing profile...");
                        _a.label = 1;
                    case 1:
                        _a.trys.push([1, 4, , 5]);
                        profileService = this.injector.get(ProfileService);
                        return [4 /*yield*/, profileService.refresh()];
                    case 2:
                        _a.sent(); // Ensure profile source is updated
                        // Re-fetch profile, create user, and emit on subject
                        return [4 /*yield*/, this.fetchProfileAndEmitUser()];
                    case 3:
                        // Re-fetch profile, create user, and emit on subject
                        _a.sent();
                        return [3 /*break*/, 5];
                    case 4:
                        error_2 = _a.sent();
                        if (isDevMode()) {
                            console.error("AuthService: Error during refresh:", error_2);
                        }
                        router = this.injector.get(Router);
                        router.navigate(['/error'], { queryParams: { reason: 'refresh_failed' } });
                        return [3 /*break*/, 5];
                    case 5: return [2 /*return*/];
                }
            });
        });
    };
    AuthService.prototype.clearLocalStorage = function () {
        var _this = this;
        forIn(this.windowService.localStorage, function (value, key) {
            if (key.startsWith('msal.') || key.startsWith('{\"authority\":') || key.startsWith('returnUrl')) {
                _this.windowService.localStorage.removeItem(key);
            }
        });
    };
    AuthService.prototype.ensureValidToken = function () {
        return tslib_1.__awaiter(this, void 0, void 0, function () {
            var account, resource, error_3;
            return tslib_1.__generator(this, function (_a) {
                switch (_a.label) {
                    case 0: return [4 /*yield*/, this.initMSAL()];
                    case 1:
                        _a.sent();
                        account = this.msalService.getAccount();
                        if (!account) {
                            if (isDevMode()) {
                                console.log('No account found - redirecting to login');
                            }
                            this.login();
                            return [2 /*return*/];
                        }
                        resource = [this.windowService.localStorage.getItem('msal.resource') || ''];
                        if (!resource[0]) {
                            return [2 /*return*/]; // No resource to validate
                        }
                        _a.label = 2;
                    case 2:
                        _a.trys.push([2, 4, , 5]);
                        return [4 /*yield*/, this.getBearerToken(resource).toPromise()];
                    case 3:
                        _a.sent();
                        this._loggedIn = true;
                        return [3 /*break*/, 5];
                    case 4:
                        error_3 = _a.sent();
                        if (isDevMode()) {
                            console.error('Token validation failed:', error_3);
                        }
                        this.clearLocalStorage();
                        this.login();
                        return [3 /*break*/, 5];
                    case 5: return [2 /*return*/];
                }
            });
        });
    };
    Object.defineProperty(AuthService.prototype, "isLoggedIn", {
        get: function () {
            return this._loggedIn;
        },
        enumerable: true,
        configurable: true
    });
    Object.defineProperty(AuthService.prototype, "userInfo", {
        get: function () {
            return this._userInfoSubject.getValue();
        },
        enumerable: true,
        configurable: true
    });
    Object.defineProperty(AuthService.prototype, "userInfoSubject", {
        get: function () {
            return this._userInfoSubject.asObservable();
        },
        enumerable: true,
        configurable: true
    });
    AuthService.prototype.setupBroadcastListeners = function () {
        var _this = this;
        // Use a more robust approach to handle broadcast events within NgZone
        this.broadcastService.subscribe('msal:loginSuccess', function (payload) {
            // Call handleLoginSuccess when MSAL signals success via broadcast
            _this.zone.run(function () { return _this.handleLoginSuccess(payload); });
        });
        this.broadcastService.subscribe('msal:loginFailure', function (error) {
            _this.zone.run(function () {
                _this.loginInProgress = false;
                sessionStorage.removeItem('login_in_progress');
                if (isDevMode()) {
                    console.error('Login Failure:', error);
                }
                // Clean up any existing token state on login failure
                _this.clearLocalStorage();
                // Try to redirect to login again
                _this.login();
            });
        });
        this.broadcastService.subscribe('msal:acquireTokenFailure', function (error) {
            _this.zone.run(function () {
                if (isDevMode()) {
                    console.error('Token Acquisition Failure:', error);
                }
                if (error && (error.errorCode === 'AADSTS160021' || error.errorCode === 'AADSTS65001')) {
                    console.warn('Forcing login due to critical acquire token failure:', error.errorCode);
                    _this._loggedIn = false;
                    _this.clearLocalStorage();
                    _this.login();
                }
                else if (error && error.name === 'InteractionRequiredAuthError') {
                    console.warn('Interaction required during silent token acquisition.', error);
                }
            });
        });
    };
    AuthService.prototype.handleLoginSuccess = function (response) {
        var _this = this;
        // Prevent double execution of this logic
        if (this._loginSuccessInProgress) {
            if (isDevMode()) {
                console.log('handleLoginSuccess already in progress, ignoring duplicate call.', response);
            }
            return;
        }
        this._loginSuccessInProgress = true;
        var isSessionRestore = response === null; // Determine if this is a restore
        if (isDevMode()) {
            console.log("handleLoginSuccess started. Is session restore: " + isSessionRestore, response);
        }
        // Clear login in progress flag from storage and memory *before* checking account
        sessionStorage.removeItem('login_in_progress');
        this.loginInProgress = false;
        // Check/Update account info *after* successful redirect processing OR on session restore
        this.checkoutAccount(); // Updates _loggedIn based on msalService.getAccount()
        if (this._loggedIn) {
            if (isDevMode()) {
                console.log('User is considered logged in, proceeding to fetch profile.');
            }
            // Show spinner before starting profile fetch
            this.spinnerService.start();
            // Always fetch a fresh profile after login success
            // Chain the flag reset after the promise settles
            var cleanup_1 = function () {
                _this._loginSuccessInProgress = false; // Reset flag AFTER profile fetch completes or fails
                _this.spinnerService.stop();
                if (isDevMode()) {
                    console.log('handleLoginSuccess cleanup executed.');
                }
            };
            // Pass the restore flag to the profile fetch function
            this.fetchProfileAndEmitUser(isSessionRestore).then(function () {
                cleanup_1();
                if (isDevMode()) {
                    console.log('Profile fetched and user emitted successfully after login/session restore.');
                }
            }, function (err) {
                console.error("Error during fetchProfileAndEmitUser called from handleLoginSuccess:", err);
                cleanup_1(); // Run cleanup even on error
            });
        }
        else {
            // If not logged in (e.g., checkoutAccount failed), reset the flags immediately
            this._loginSuccessInProgress = false;
            if (isDevMode()) {
                console.warn("handleLoginSuccess called, but user is NOT considered logged in after checkoutAccount().");
            }
            // Stop spinner if it was started
            this.spinnerService.stop();
        }
    };
    AuthService.prototype.fetchProfileAndEmitUser = function (isSessionRestore) {
        if (isSessionRestore === void 0) { isSessionRestore = false; }
        return tslib_1.__awaiter(this, void 0, void 0, function () {
            var callId, account, ui;
            var _this = this;
            return tslib_1.__generator(this, function (_a) {
                callId = Math.random().toString(36).substring(2, 8);
                account = this.msalService.getAccount();
                if (!account) {
                    throw new Error("MSAL account not found during profile fetch.");
                }
                if (!account.idTokenClaims || !account.userName) {
                    this.clearLocalStorage();
                    this.login();
                    throw new Error("Invalid token claims");
                }
                ui = {
                    userName: account.userName,
                    profile: account.idTokenClaims
                };
                return [2 /*return*/, new Promise(function (resolve, reject) {
                        // Get ProfileService via injector
                        var profileService = _this.injector.get(ProfileService);
                        profileService.profile.pipe(take(1), catchError(function (error) {
                            if (error instanceof HttpErrorResponse && error.status === 401) {
                                _this.loginInProgress = false;
                                sessionStorage.removeItem('login_in_progress');
                                _this.login();
                                return EMPTY;
                            }
                            // Get Router via injector
                            var router = _this.injector.get(Router);
                            router.navigate(['/error'], { queryParams: { reason: 'profile_fetch_failed' } });
                            return throwError(error);
                        })).subscribe({
                            next: function (profile) {
                                if (profile) {
                                    try {
                                        var userInfo_1 = new VideoPortalUser(ui, profile);
                                        _this.zone.run(function () {
                                            _this._userInfoSubject.next(userInfo_1);
                                            // Get AppInsightsService via injector
                                            var ai = _this.injector.get(AppInsightsService);
                                            ai.setAuthenticatedUserContext(userInfo_1.username, userInfo_1.councilName, true);
                                            if (!userInfo_1.hasAgreedToTermsAndConditions) {
                                                // Get Router via injector
                                                var router = _this.injector.get(Router);
                                                if (router.url !== '/terms-and-conditions') {
                                                    router.navigate(['/terms-and-conditions']);
                                                }
                                            }
                                            else {
                                                // Only navigate using ReturnUrlService if this is NOT a session restore
                                                if (!isSessionRestore) {
                                                    if (isDevMode()) {
                                                        console.log('Login success (not restore): Clearing return URL and navigating.');
                                                    }
                                                    // Get ReturnUrlService via injector
                                                    var returnUrlService = _this.injector.get(ReturnUrlService);
                                                    returnUrlService.clear(); // Clear the URL
                                                    returnUrlService.navigate(); // Navigate (will default to home if URL was cleared)
                                                }
                                                else {
                                                    if (isDevMode()) {
                                                        console.log('Session restore: Skipping navigation via ReturnUrlService.');
                                                    }
                                                }
                                            }
                                        });
                                        resolve();
                                    }
                                    catch (error) {
                                        console.error("Error creating user info:", error);
                                        reject(error);
                                    }
                                }
                                else {
                                    console.warn("Profile fetch did not return a profile.");
                                    resolve();
                                }
                            },
                            error: function (err) {
                                console.error("Profile fetch subscription received error:", err);
                                reject(err);
                            }
                        });
                    })];
            });
        });
    };
    // New private method to encapsulate callback registration
    AuthService.prototype.registerRedirectCallback = function () {
        return tslib_1.__awaiter(this, void 0, void 0, function () {
            var _this = this;
            return tslib_1.__generator(this, function (_a) {
                this.msalService.handleRedirectCallback(function (authError, response) {
                    // No longer need the _redirectProcessed flag check here
                    _this.zone.run(function () {
                        // Let MSAL process the hash, but DO NOT call handleLoginSuccess from here.
                        // Rely on the 'msal:loginSuccess' broadcast instead.
                        if (authError) {
                            console.error('Redirect Error: ', authError.errorMessage);
                            // Handle specific errors if needed
                        }
                        else if (response) {
                            if (isDevMode()) {
                                // Log success but don't act on it here
                                console.log('Redirect Success via handleRedirectCallback (Processing ONLY, logic triggered by broadcast).', response);
                            }
                        }
                    });
                });
                return [2 /*return*/];
            });
        });
    };
    Object.defineProperty(AuthService.prototype, "isLoginInProgress", {
        get: function () {
            return this.loginInProgress;
        },
        enumerable: true,
        configurable: true
    });
    Object.defineProperty(AuthService.prototype, "initializationPromise", {
        // Getter for the initialization promise
        get: function () {
            return this.msalInitPromise;
        },
        enumerable: true,
        configurable: true
    });
    Object.defineProperty(AuthService.prototype, "isLoginSuccessInProgress", {
        // Getter for login success status
        get: function () {
            return this._loginSuccessInProgress;
        },
        enumerable: true,
        configurable: true
    });
    return AuthService;
}());
export { AuthService };
